import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { HttpService } from './http.service';
import {
  Attribute,
  AttributeOption,
  AttributesListResponse,
  AttributeSubmitPayload,
  CreateGs1AttributePayload,
  DEFAULT_ATTRIBUTES_CURRENT_PAGE,
  DEFAULT_ATTRIBUTES_PER_PAGE,
  ExportAttributesPayload,
  Gs1AiDefinition,
} from '../../store/entities/models/attribute.model';
import {
  transformCamelToSnake,
  transformSnakeToCamel,
} from '../../store/utils/transformers';
import { ApiTableFilter } from '../../shared/models/table-filter.model';
import { toAttributesApiFilters } from './attributes-filter.util';

export interface AttributeDefinition {
  uuid: string;
  name: string;
  code: string;
  type?: string;
  fieldType?: string;
  description?: string | null;
  options?: Array<{ name: string; code: string }>;
  isGs1?: boolean;
  gs1AiDefinition?: { category?: string };
}

interface AttributeListResponse {
  data: Record<string, unknown>[];
}

export interface AttributesListParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort?: string;
  sort_direction?: string;
  filters?: ApiTableFilter[];
  filter_link?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AttributesService {
  private http = inject(HttpService);

  getAttributes(): Observable<AttributeDefinition[]> {
    return this.http
      .get<AttributeListResponse>('/attributes', { page: 1, per_page: 5000 })
      .pipe(
        map((response) =>
          (response.data ?? []).map(
            (attribute) =>
              transformSnakeToCamel(attribute) as AttributeDefinition,
          ),
        ),
      );
  }

  getPaginatedAttributes(
    params: AttributesListParams = {},
  ): Observable<AttributesListResponse> {
    const queryParams: Record<string, unknown> = {
      page: params.page ?? DEFAULT_ATTRIBUTES_CURRENT_PAGE,
      per_page: params.per_page ?? DEFAULT_ATTRIBUTES_PER_PAGE,
      search: params.search ?? '',
      sort: params.sort ?? 'created_at',
      sort_direction: params.sort_direction ?? 'desc',
    };

    if (params.filters?.length) {
      queryParams.filters = toAttributesApiFilters(params.filters);
      queryParams.filterLink = params.filter_link ?? 'and';
    }

    return this.http
      .get<Record<string, unknown>>('/attributes', queryParams)
      .pipe(map((response) => this.mapAttributesListResponse(response)));
  }

  getAttributeOptions(attributeId: string): Observable<AttributeOption[]> {
    return this.http
      .get<Record<string, unknown>>(
        `/attributes/${attributeId}/attribute-options`,
        { per_page: 5000, page: 1 },
      )
      .pipe(map((response) => this.mapOptionsResponse(response)));
  }

  createAttribute(
    payload: AttributeSubmitPayload,
  ): Observable<{ data: Attribute }> {
    return this.http
      .post<Record<string, unknown>>(
        '/attributes',
        this.mapCreateAttributeRequest(payload),
      )
      .pipe(map((response) => this.mapAttributeResponse(response)));
  }

  updateAttribute(
    attributeId: string,
    payload: AttributeSubmitPayload,
  ): Observable<{ data: Attribute }> {
    return this.http
      .put<Record<string, unknown>>(
        `/attributes/${attributeId}`,
        this.mapUpdateAttributeRequest(payload),
      )
      .pipe(map((response) => this.mapAttributeResponse(response)));
  }

  deleteAttribute(attributeId: string): Observable<unknown> {
    return this.http.delete<unknown>(`/attributes/${attributeId}`);
  }

  exportAttributes(
    payload: ExportAttributesPayload,
  ): Observable<{ id?: string | number; data?: { id?: string | number } }> {
    const filters = toAttributesApiFilters(payload.filters ?? []);
    const body: Record<string, unknown> = {
      filters,
      selected_attributes: payload.selectedAttributes ?? [],
      all_selected: payload.allSelected,
    };

    if (payload.search) {
      body.search = payload.search;
    }

    if (filters.length) {
      body.filter_link = payload.filterLink ?? 'and';
    }

    return this.http.post<{
      id?: string | number;
      data?: { id?: string | number };
    }>('/attributes/export', body);
  }

  getGs1AiDefinitions(): Observable<Gs1AiDefinition[]> {
    return this.http
      .get<unknown>('/gs1/ai-definitions')
      .pipe(map((response) => this.mapGs1DefinitionsResponse(response)));
  }

  createGs1Attribute(
    payload: CreateGs1AttributePayload,
  ): Observable<{ data: Attribute }> {
    return this.http
      .post<Record<string, unknown>>('/gs1/attributes', {
        ai_code: payload.aiCode,
        name: payload.name,
        description: payload.description ?? '',
      })
      .pipe(map((response) => this.mapAttributeResponse(response)));
  }

  private mapCreateAttributeRequest(
    payload: AttributeSubmitPayload,
  ): Record<string, unknown> {
    const body: Record<string, unknown> = {
      fieldType: payload.fieldType,
      dataType: payload.dataType ?? 'STRING',
      name: payload.name,
      options: payload.options ? this.mapOptionsToRequest(payload.options) : [],
    };

    if (payload.description !== undefined) {
      body.description = payload.description;
    }

    if (payload.modifiable !== undefined) {
      body.modifiable = payload.modifiable;
    }

    return transformCamelToSnake(body);
  }

  private mapUpdateAttributeRequest(
    payload: AttributeSubmitPayload,
  ): Record<string, unknown> {
    const body: Record<string, unknown> = {
      name: payload.name,
      fieldType: payload.fieldType,
      dataType: payload.dataType ?? 'STRING',
      options: payload.options ? this.mapOptionsToRequest(payload.options) : [],
    };

    if (payload.uuid !== undefined) {
      body.uuid = payload.uuid;
    }

    if (payload.id !== undefined) {
      body.id = payload.id;
    }

    if (payload.description !== undefined) {
      body.description = payload.description;
    }

    if (payload.modifiable !== undefined) {
      body.modifiable = payload.modifiable;
    }

    return transformCamelToSnake(body);
  }

  private mapOptionsToRequest(
    options: AttributeOption[],
  ): Array<Record<string, unknown>> {
    return options.map(({ value, label, order, uuid }) => ({
      code: value,
      name: label,
      order,
      ...(uuid ? { uuid } : {}),
    }));
  }

  private mapAttributesListResponse(
    response: Record<string, unknown>,
  ): AttributesListResponse {
    const transformed = transformSnakeToCamel(response);
    const data = Array.isArray(transformed.data)
      ? transformed.data.map((attribute) =>
          this.mapAttribute(attribute as Record<string, unknown>),
        )
      : [];

    return {
      data,
      meta: transformed.meta as AttributesListResponse['meta'],
    };
  }

  private mapAttributeResponse(
    response: Record<string, unknown>,
  ): { data: Attribute } {
    const transformed = transformSnakeToCamel(response);

    return {
      data: this.mapAttribute(
        transformed.data as Record<string, unknown>,
      ),
    };
  }

  private mapOptionsResponse(
    response: Record<string, unknown>,
  ): AttributeOption[] {
    const transformed = transformSnakeToCamel(response);
    const { data } = transformed;

    return Array.isArray(data)
      ? data.map((option) =>
          this.mapOption(option as Record<string, unknown>),
        )
      : [];
  }

  private mapGs1DefinitionsResponse(
    response: unknown,
  ): Gs1AiDefinition[] {
    return this.extractGs1DefinitionList(response)
      .filter(
        (definition): definition is Record<string, unknown> =>
          Boolean(definition) && typeof definition === 'object',
      )
      .map((definition) => this.mapGs1Definition(definition))
      .filter((definition) => definition.aiCode);
  }

  private extractGs1DefinitionList(response: unknown): unknown[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (!response || typeof response !== 'object') {
      return [];
    }

    const record = response as Record<string, unknown>;
    const nested =
      record.data ?? record.definitions ?? record.aiDefinitions;

    if (Array.isArray(nested)) {
      return nested;
    }

    if (nested && typeof nested === 'object') {
      const nestedRecord = nested as Record<string, unknown>;
      const inner =
        nestedRecord.data ??
        nestedRecord.definitions ??
        nestedRecord.aiDefinitions;

      if (Array.isArray(inner)) {
        return inner;
      }
    }

    return [];
  }

  private mapGs1Definition(
    definition: Record<string, unknown>,
  ): Gs1AiDefinition {
    const transformed = transformSnakeToCamel(definition);

    return {
      aiCode: String(
        transformed.aiCode ?? transformed.code ?? '',
      ),
      aiTitle: String(
        transformed.aiTitle ??
          transformed.title ??
          transformed.name ??
          '',
      ),
      dataFormat: transformed.dataFormat as string | undefined,
      category: transformed.category as string | undefined,
      pathSegmentType: transformed.pathSegmentType as string | undefined,
      components: transformed.components,
      linters: transformed.linters,
    };
  }

  private mapAttribute(attribute: Record<string, unknown>): Attribute {
    const {
      options,
      additionalNotes,
      isGs1,
      notes,
      ...rest
    } = attribute;

    const mapped: Record<string, unknown> = {
      ...rest,
      notes: notes ?? additionalNotes,
      isGS1: attribute.isGS1 ?? isGs1,
    };

    // The API returns both `field_type` (string) and `fieldType` (object with code/name).
    // After transformSnakeToCamel, the object overwrites the string under the same key.
    // React uses pickAndRename which only picks `field_type` → string value.
    // Match React: extract the string code from the object, or keep as-is if already a string.
    if (mapped.fieldType && typeof mapped.fieldType === 'object') {
      mapped.fieldType = (mapped.fieldType as Record<string, unknown>).code ?? '';
    }

    if (mapped.dataType && typeof mapped.dataType === 'object') {
      mapped.dataType = (mapped.dataType as Record<string, unknown>).code ?? '';
    }

    if (Array.isArray(options)) {
      mapped.options = options.map((option) =>
        this.mapOption(option as Record<string, unknown>),
      );
    }

    return mapped as unknown as Attribute;
  }

  private mapOption(option: Record<string, unknown>): AttributeOption {
    const mapped: AttributeOption = {
      value: String(option.value ?? option.code ?? ''),
      label: String(option.label ?? option.name ?? ''),
      order: Number(option.order ?? 0),
    };

    if (option.uuid !== undefined) {
      mapped.uuid = String(option.uuid);
    }

    return mapped;
  }
}
